import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';
import { useAuth } from '../hooks/useAuth';
import Wordmark from '../components/Wordmark';

/**
 * SharePage — landing page for public share links (SHARING_SPEC §6).
 *
 * URL: /share/:token
 *
 * Flow:
 *   1. Recipient pastes / clicks the public link.
 *   2. If not signed in, render an inline sign-in widget. No
 *      anonymous access — every joiner ends up in the editors/viewers
 *      ACL with an identifiable uid.
 *   3. Once signed in, automatically call joinViaShareToken with
 *      the URL token. Server reads the shareTokens index, adds the
 *      caller to the appropriate ACL array, writes a sharedWithMe
 *      pointer, returns the redirect path.
 *   4. Full-page reload to the redirect (same trick as
 *      AcceptInvitePage to dodge the React/Firestore listener race
 *      after sign-in).
 *
 * Error states (token revoked / regenerated / underlying resource
 * deleted) surface a friendly error + "go to listicity" button.
 */
export default function SharePage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const {
    user,
    signInWithGoogle,
    sendMagicLink,
  } = useAuth();

  const [status, setStatus] = useState('awaiting-signin'); // 'awaiting-signin' | 'joining' | 'success' | 'error'
  const [errorMsg, setErrorMsg] = useState('');
  const [magicState, setMagicState] = useState('idle');
  const [magicEmail, setMagicEmail] = useState('');
  const joinCalledRef = useRef(false);

  // Once signed in, fire joinViaShareToken automatically.
  useEffect(() => {
    if (!user || !token) return;
    if (joinCalledRef.current) return;
    joinCalledRef.current = true;
    setStatus('joining');
    (async () => {
      try {
        const fn = httpsCallable(functions, 'joinViaShareToken');
        const res = await fn({ token });
        const data = res?.data || {};
        if (data?.ok && data?.redirectTo) {
          setStatus('success');
          setTimeout(() => {
            window.location.href = data.redirectTo;
          }, 600);
        } else {
          setStatus('error');
          setErrorMsg('Could not open this share.');
        }
      } catch (err) {
        console.error('[SharePage] join failed', err);
        setStatus('error');
        setErrorMsg(err?.message || 'Could not open this share.');
      }
    })();
  }, [user, token]);

  const handleMagicSubmit = async (e) => {
    e.preventDefault();
    if (!/\S+@\S+\.\S+/.test(magicEmail.trim())) {
      setErrorMsg('Please enter a valid email');
      return;
    }
    setErrorMsg('');
    setMagicState('sending');
    try {
      await sendMagicLink(
        magicEmail.trim(),
        window.location.origin + window.location.pathname,
      );
      setMagicState('sent');
    } catch (err) {
      console.error('[SharePage] magic-link request failed', err);
      if (err && err.code === 'account-suspended') {
        setErrorMsg('This email was used to delete a listicity account in the last 7 days. Check your inbox for the recovery link, or wait until the 7-day window ends.');
      } else {
        setErrorMsg('Could not send the link. Try Google sign-in instead.');
      }
      setMagicState('idle');
    }
  };

  return (
    <div style={{
      minHeight: '100vh', display: 'flex',
      alignItems: 'center', justifyContent: 'center',
      background: 'var(--bg)', padding: '20px',
    }}>
      <div style={{
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius)',
        padding: '40px 44px 36px',
        maxWidth: '420px',
        width: '100%',
        textAlign: 'center',
      }}>
        <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'center' }}>
          <Wordmark fontSize={26} scaleWithUi={false} />
        </div>

        <div style={{
          fontFamily: 'var(--mono)',
          fontSize: 15,
          color: 'var(--text)',
          fontWeight: 600,
          marginBottom: 8,
        }}>
          someone shared something with you
        </div>
        <div style={{
          fontFamily: 'var(--mono)',
          fontSize: 13,
          color: 'var(--text-dim)',
          marginBottom: 24,
          lineHeight: 1.6,
        }}>
          {status === 'awaiting-signin' && !user
            ? 'sign in to open the shared board or list — your account stays in the share so you can come back later.'
            : status === 'joining'
              ? 'adding you…'
              : status === 'success'
                ? '✓ taking you in…'
                : 'this share link may have been revoked or the item deleted.'}
        </div>

        {status === 'awaiting-signin' && !user && (
          <SignInBody
            magicState={magicState}
            magicEmail={magicEmail}
            setMagicEmail={(v) => { setMagicEmail(v); setErrorMsg(''); }}
            onGoogle={signInWithGoogle}
            onMagicSubmit={handleMagicSubmit}
            onMagicReset={() => { setMagicState('idle'); setMagicEmail(''); }}
            errorMsg={errorMsg}
          />
        )}

        {status === 'error' && (
          <>
            {errorMsg && (
              <div style={{
                fontFamily: 'var(--mono)',
                fontSize: 12,
                color: 'var(--danger, #d04a4a)',
                lineHeight: 1.6,
                marginBottom: 16,
              }}>
                {errorMsg}
              </div>
            )}
            <button
              type="button"
              onClick={() => navigate('/', { replace: true })}
              style={{
                background: 'transparent',
                border: '1px solid var(--border-bright)',
                color: 'var(--text-dim)',
                fontFamily: 'var(--mono)',
                fontSize: 13,
                padding: '10px 16px',
                borderRadius: 'var(--radius)',
                cursor: 'pointer',
              }}
            >
              go to listicity
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function SignInBody({
  magicState, magicEmail, setMagicEmail,
  onGoogle, onMagicSubmit, onMagicReset, errorMsg,
}) {
  if (magicState === 'sent') {
    return (
      <div style={{ textAlign: 'left' }}>
        <div style={{
          fontFamily: 'var(--mono)',
          fontSize: 16,
          color: 'var(--accent)',
          fontWeight: 600,
          marginBottom: 10,
          textAlign: 'center',
        }}>
          ✓ check your inbox
        </div>
        <div style={{
          fontFamily: 'var(--mono)',
          fontSize: 13,
          color: 'var(--text-dim)',
          lineHeight: 1.6,
          marginBottom: 16,
          textAlign: 'center',
        }}>
          we sent a sign-in link to <span style={{ color: 'var(--text)' }}>{magicEmail}</span>.
          click it from this device — you'll come back here and the share will open.
        </div>
        <div style={{ textAlign: 'center' }}>
          <button
            type="button"
            onClick={onMagicReset}
            style={{
              background: 'none',
              border: '1px solid var(--border-bright)',
              color: 'var(--text-dim)',
              fontFamily: 'var(--mono)',
              fontSize: 13,
              padding: '8px 14px',
              borderRadius: 'var(--radius)',
              cursor: 'pointer',
            }}
          >
            use a different option
          </button>
        </div>
      </div>
    );
  }
  return (
    <>
      <button
        onClick={onGoogle}
        style={{
          width: '100%', boxSizing: 'border-box',
          padding: '12px 16px',
          background: 'var(--accent)',
          color: 'var(--bg)',
          border: '1px solid var(--accent)',
          borderRadius: 'var(--radius)',
          fontFamily: 'var(--mono)',
          fontSize: 14,
          fontWeight: 600,
          cursor: 'pointer',
        }}
      >
        Sign in with Google
      </button>

      <div style={{
        margin: '14px 0', display: 'flex', alignItems: 'center', gap: 10,
        fontSize: 12, color: 'var(--text-dimmer)', textTransform: 'uppercase', letterSpacing: '1px',
      }}>
        <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
        or with a different email
        <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
      </div>

      <form onSubmit={onMagicSubmit}>
        <input
          type="email"
          value={magicEmail}
          onChange={(e) => setMagicEmail(e.target.value)}
          placeholder="your@email.com"
          autoComplete="email"
          disabled={magicState === 'sending'}
          style={{
            width: '100%', boxSizing: 'border-box',
            padding: '11px 14px',
            background: 'var(--bg)',
            border: '1px solid var(--border-bright)',
            borderRadius: 'var(--radius)',
            color: 'var(--text)',
            fontFamily: 'var(--mono)',
            fontSize: 14,
            outline: 'none',
            marginBottom: 8,
          }}
        />
        <button
          type="submit"
          disabled={magicState === 'sending'}
          style={{
            width: '100%', boxSizing: 'border-box',
            padding: '12px 16px',
            background: 'transparent',
            color: 'var(--accent)',
            border: '1px solid var(--accent-dim)',
            borderRadius: 'var(--radius)',
            fontFamily: 'var(--mono)',
            fontSize: 14,
            cursor: 'pointer',
          }}
        >
          {magicState === 'sending' ? 'sending…' : 'Email me a sign-in link'}
        </button>
      </form>

      {errorMsg && (
        <div style={{
          marginTop: 10,
          color: 'var(--danger)',
          fontFamily: 'var(--mono)',
          fontSize: 12,
        }}>
          {errorMsg}
        </div>
      )}
    </>
  );
}
